import * as React from "react";
import { CommonHelper } from "../../../../../../../react-core/Helpers/CommonHelper";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { PartLoading } from "../../../../../../../react-core/Html/PartLoading";
import { PercentageTextBoxEditor } from "../../../../../../../react-core/Html/PercentageTextBoxEditor";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { InvestmentAllocation } from "../../../../../../../Wrap/Models/Domains/Investment/InvestmentAllocation";
import { PensionTransfer } from "../../../../../../../Wrap/Models/Models/Processes/Quote/Pension/PensionTransfer";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { User } from "../../../../../../../Wrap/Models/Models/User/User";
import { GENERIC_RESTRICTIONS_MESSAGE } from "../../../../../../Shared/Common/UserMessages";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { Table } from "../../../../../../Shared/Table/Table";
import { UserStore } from "../../../../../Stroes/Variable";
import { RestrictionStore } from "../../Restrictions/Variable";
import { getAssetsToSellTotalForIncomeFromIncome } from "../IncomePaymentDetails/Helper/IncomePaymentHelper";
import { calculateChooseAssetsToSellTotal } from "./Helper/DrawdownDetailHelper";
import { ChooseAssetsToSellFor } from "./Resource/Contents";
import { ChooseAssetsToSellAssets } from "./Store/DrawdownCalculationStoreStatic";

interface IChooseAssetsToSellTableProps extends IProcessComponentProps {
    data: InvestmentAllocation[];
    tableData: ChooseAssetsToSellAssets[];
    modelData: DrawdownModel;
    reason: ChooseAssetsToSellFor;
    processType: ProcessType;
    gadReviewtotalValue?: number;
    transferData?: PensionTransfer[];
}

interface IChooseAssetsToSellTableState {
    userInfo: User;
}

export class ChooseAssetsToSellTable extends ProcessComponent<
    IChooseAssetsToSellTableProps,
    IChooseAssetsToSellTableState
> {
    constructor(props: any) {
        super(props);
        this.state = {
            userInfo: UserStore.getUserInfo(),
        };
    }

    private onAssetsChanges(row: ChooseAssetsToSellAssets, v: number) {
        const i = this.props.data.findIndex((ele) => ele.ProductId == row.Id);
        const _data = this.props.data.concat();
        if (i > -1) {
            _data[i].Percentage = v;
        } else {
            _data.push({ Name: row.Assets, Percentage: v, ProductId: row.Id, Type: row.ProductType });
        }
        this.props.onChange(_data);
    }

    isValid() {
        const total = this.props.data?.reduce((sum, curr) => {
            return (sum = sum + curr.Percentage);
        }, 0);
        if (total !== 100) {
            this.props.showErrorMsgPopUp(["Please enter values against the assets to disinvest that total 100%"]);
        }
        return super.isValid() && total == 100;
    }

    public render(): JSX.Element {
        const { data, tableData, modelData, reason, processType, transferData } = this.props;
        const total = data?.reduce((sum, curr) => {
            return (sum = sum + curr.Percentage);
        }, 0);

        let totalValue;
        if (processType == ProcessType.GadReview) {
            totalValue = this.props.gadReviewtotalValue;
        } else {
            if (modelData) {
                totalValue = calculateChooseAssetsToSellTotal(modelData, reason);
            } else if (transferData?.length > 0) {
                totalValue = getAssetsToSellTotalForIncomeFromIncome(
                    transferData[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
                );
            }
        }

        if (isNullOrEmpty(tableData)) {
            return <PartLoading isLoading={true} />;
        }
        let _tableData = tableData.concat();

        if (RestrictionStore.sellDisabled()) {
            _tableData = [];
        }

        let restrictedMessage = "An account with suspended or restricted activities cannot setup sell investments";

        if (this.state.userInfo != null && this.state.userInfo.IsAdviser) {
            restrictedMessage = GENERIC_RESTRICTIONS_MESSAGE;
        }

        return (
            <>
                {RestrictionStore.sellDisabled() && <Alert alertType={AlertType.Error} message={restrictedMessage} />}
                <Table
                    data={_tableData}
                    col={[
                        {
                            title: "Assets",
                            key: "Assets",
                            render: (data, row: ChooseAssetsToSellAssets, index) => {
                                return (
                                    <a
                                        className={CommonHelper.isNullOrEmpty(row.FactsheetUrl) ? "" : "hyper-link"}
                                        href={row.FactsheetUrl}
                                        target="_blank"
                                    >
                                        {data}
                                    </a>
                                );
                            },
                        },
                        {
                            title: "Current estimated value",
                            key: "MarketValue",
                            render: (data, row, index) => {
                                return MoneyDisplayHelper.getMoneyFormat(data);
                            },
                        },
                        {
                            title: "Percentage of disinvestment amount",
                            key: "",
                            render: (data, row: ChooseAssetsToSellAssets, index) => {
                                const percentage =
                                    this.props.data.find((ele) => ele.ProductId == row.Id)?.Percentage || null;
                                return (
                                    <PercentageTextBoxEditor
                                        value={percentage}
                                        onChange={(v) => this.onAssetsChanges(row, v)}
                                        maxNumberOfDecimalPlaces={{
                                            errorMessage: "only allow two decimal places",
                                            value: 2,
                                        }}
                                        disabled={row.DisinvestAll}
                                        validationRules={[
                                            {
                                                errorMessage:
                                                    "Please enter a value to disinvest that is less than 100% and equates to less than 100% of the current estimated value of the fund, or select to disinvest all units from the asset.",
                                                validator: (v) => {
                                                    return v <= 100;
                                                },
                                            },
                                            {
                                                errorMessage:
                                                    "Product allocation value exceeds 95% of total holding value",
                                                validator: (v) => {
                                                    if (modelData?.DrawdownType == DrawdownType.Regular) {
                                                        return true;
                                                    }
                                                    const amount = (v || 0) * totalValue * 0.01;
                                                    return amount < row.MarketValue?.Amount * 0.95 || row.DisinvestAll;
                                                },
                                            },
                                            {
                                                errorMessage:
                                                    "You have entered a value to disinvest that is greater than the current estimated value of the asset.",
                                                validator: (v) => {
                                                    if (modelData?.DrawdownType == DrawdownType.Regular) {
                                                        return true;
                                                    }
                                                    let amount = (v || 0) * totalValue * 0.01;
                                                    amount = Number(amount.toFixed(2));
                                                    return amount <= row.MarketValue?.Amount;
                                                },
                                            },
                                            {
                                                errorMessage:
                                                    "You have entered a value that is equal to the current estimated value of the asset. You may either choose to 'disinvest all' or reduce the value that you wish to disinvest.",
                                                validator: (v) => {
                                                    if (modelData?.DrawdownType == DrawdownType.Regular) {
                                                        return true;
                                                    }
                                                    let amount = (v || 0) * totalValue * 0.01;
                                                    amount = Number(amount.toFixed(2));
                                                    return (
                                                        amount !== row.MarketValue?.Amount ||
                                                        (amount == row.MarketValue?.Amount && row.DisinvestAll)
                                                    );
                                                },
                                            },
                                        ]}
                                        infoText={
                                            modelData?.DrawdownType != DrawdownType.Regular
                                                ? MoneyDisplayHelper.getMoneyFormat({
                                                      Amount: (percentage || 0) * totalValue * 0.01,
                                                  })
                                                : null
                                        }
                                    />
                                );
                            },
                        },
                        modelData?.DrawdownType != DrawdownType.Regular
                            ? {
                                  thCssWrapper: "cell-right pr-0",
                                  tdCssWrapper: "cell-right pr-0",
                                  title: "Disinvest all",
                                  key: "DisinvestAll",
                                  render: (data, row: ChooseAssetsToSellAssets, index) => {
                                      const percentage =
                                          this.props.data.find((ele) => ele.ProductId == row.Id)?.Percentage || 0;
                                      return (
                                          <CheckboxEditor
                                              cssWrapper={"square-checkbox mb-0"}
                                              onChange={(v) => {
                                                  this.onAssetsChanges(row, v ? 100 : percentage);

                                                  this.props.tableData[index].DisinvestAll = v;
                                              }}
                                              value={data}
                                          />
                                      );
                                  },
                              }
                            : null,
                    ]}
                    TotalBody={[
                        { data: "Total", colSpan: 2 },
                        {
                            data: <span className={total == 100 ? "text-positive" : "text-text"}>{total}%</span>,
                            colSpan: 2,
                        },
                    ]}
                />
            </>
        );
    }
}
